import { useMemo } from "react";
import { useInRouterContext, useLocation } from "react-router";

import { ROUTES } from "@/constants/routes";
import {
	type EventFiltersRegistry,
	EventFiltersRegistryContext,
} from "@/context/event-filters-registry";
import {
	eventsReducer,
	type EventsState,
	initialEventsState,
} from "@/context/events";
import { usePersistedEventsFilters } from "@/features/events/hooks/usePersistedEventsFilters";
import { useValidateLocationZoneSensorFilters } from "@/features/events/hooks/useValidateLocationZoneSensorFilters";
import { EVENT_FILTER_SCOPE } from "@/features/events/types/event-filter-scope";
import { useFilterUrlSync } from "@/hooks/useFilterUrlSync";
import { useReducerWithLogger } from "@/hooks/useReducerWithLogger";

export interface EventFiltersProviderProps {
	children: React.ReactNode;
	initialAllActivityState?: Partial<EventsState>;
	initialDashboardState?: Partial<EventsState>;
}

/**
 * App-level provider holding independent filter state for Dashboard and All Activity.
 * Each page hydrates and persists its own filters (localStorage + URL) when active.
 */
export const EventFiltersProvider = ({
	children,
	initialAllActivityState,
	initialDashboardState,
}: EventFiltersProviderProps) => {
	const [dashboardState, dashboardDispatch] = useReducerWithLogger(
		eventsReducer,
		initialEventsState,
		(): EventsState => ({
			...initialEventsState,
			...(initialDashboardState ?? {}),
		}),
		import.meta.env.DEV,
	);

	const [allActivityState, allActivityDispatch] = useReducerWithLogger(
		eventsReducer,
		initialEventsState,
		(): EventsState => ({
			...initialEventsState,
			...(initialAllActivityState ?? {}),
		}),
		import.meta.env.DEV,
	);

	const registry = useMemo<EventFiltersRegistry>(
		() => ({
			allActivity: {
				dispatch: allActivityDispatch,
				state: allActivityState,
			},
			dashboard: {
				dispatch: dashboardDispatch,
				state: dashboardState,
			},
		}),
		[allActivityDispatch, allActivityState, dashboardDispatch, dashboardState],
	);

	return (
		<EventFiltersRegistryContext.Provider value={registry}>
			<DashboardFilterSync />
			<AllActivityFilterSync />
			{children}
		</EventFiltersRegistryContext.Provider>
	);
};

const DashboardFilterSync = () => {
	if (!useInRouterContext()) {
		return null;
	}

	return <DashboardFilterSyncActive />;
};

const DashboardFilterSyncActive = () => {
	const { pathname } = useLocation();
	const enabled = pathname === ROUTES.DASHBOARD;

	// Hydration order (layout effects): validate → localStorage → URL (URL wins).
	useValidateLocationZoneSensorFilters({
		enabled,
		scope: EVENT_FILTER_SCOPE.DASHBOARD,
	});
	usePersistedEventsFilters({
		enabled,
		scope: EVENT_FILTER_SCOPE.DASHBOARD,
	});
	useFilterUrlSync({
		enabled,
		includeAlertStatus: false,
		scope: EVENT_FILTER_SCOPE.DASHBOARD,
	});

	return null;
};

const AllActivityFilterSync = () => {
	if (!useInRouterContext()) {
		return null;
	}

	return <AllActivityFilterSyncActive />;
};

const AllActivityFilterSyncActive = () => {
	const { pathname } = useLocation();
	const enabled = pathname === ROUTES.ALL_ACTIVITY;

	// Hydration order (layout effects): validate → localStorage → URL (URL wins).
	useValidateLocationZoneSensorFilters({
		enabled,
		scope: EVENT_FILTER_SCOPE.ALL_ACTIVITY,
	});
	usePersistedEventsFilters({
		enabled,
		scope: EVENT_FILTER_SCOPE.ALL_ACTIVITY,
	});
	useFilterUrlSync({
		enabled,
		includeAlertStatus: true,
		scope: EVENT_FILTER_SCOPE.ALL_ACTIVITY,
	});

	return null;
};
